import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import './CreateTicket.css';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const priorities = ['Low', 'Medium', 'High', 'Critical'];

const slugify = str =>
  str.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

const CreateTicket = () => {
  const { selectedCenter, userData } = useUser();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    title: '',
    description: '',
    attachments: [],
    priority: '',
    academy: null,
    progress: {},
    status: {},
  });

  const [categories, setCategories] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const baselineRef = useRef(null);
  const userId = userData?.userId;
  const buildSubmitPayload = (fd = formData) => ({
    categoryId: fd.category ? fd.category.id : null,
    subcategoryId: fd.subcategory ? fd.subcategory.id : null,
    title: fd.title || '',
    description: fd.description || '',
    priority: fd.priority ? fd.priority.toUpperCase() : null,
    createdBy: userId || null,
    academyId: fd.academy ? fd.academy.value : null,
    attachments: (fd.attachments || []).map(f => ({
      name: f.name,
      type: f.type,
      size: f.size,
    })),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_TICKET_DROPDOWN, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });
        const data = response.data.data;
        setCategories(data.categories || []);
        const fetchedAcademies = Object.entries(data.academies || {}).map(
          ([id, name]) => ({ id: Number(id), name })
        );
        let filteredAcademies = fetchedAcademies;
        if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
          filteredAcademies = fetchedAcademies.filter((academy) =>
            userData.academyIds.includes(academy.id)
          );
        }
        setAcademies(filteredAcademies);

        setAcademyOptions(
          filteredAcademies.map((academy) => ({
            label: academy.name,
            value: academy.id,
          }))
        );

        baselineRef.current = buildSubmitPayload({
          ...formData,
          academy: null,
          attachments: [],
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCenter && academies.length > 0) {
      const selectedAcademy = academies.find((a) => a.id === Number(selectedCenter));
      if (selectedAcademy) {
        setFormData((prev) => {
          const next = {
            ...prev,
            academy: { label: selectedAcademy.name, value: selectedAcademy.id },
          };
          return next;
        });
      }
    }
  }, [selectedCenter, academies]);

  const [showCategoryInfo, setShowCategoryInfo] = useState(false);
  const [showSubcategoryInfo, setShowSubcategoryInfo] = useState(false);

  const handleCategoryClick = cat => {
    setFormData(prev => ({ ...prev, category: cat, subcategory: '' }));
    setShowCategoryInfo(false);
    setShowSubcategoryInfo(false);
  };

  const handleSubcategoryClick = sub => {
    setFormData(prev => ({ ...prev, subcategory: sub }));
    setShowSubcategoryInfo(false);
  };

  const handlePriorityClick = level =>
    setFormData(prev => ({ ...prev, priority: level }));

  const handleAcademyChange = selectedOption => {
    setFormData(prev => ({ ...prev, academy: selectedOption }));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));

    files.forEach((file) => {
      setFormData(prev => ({
        ...prev,
        progress: { ...prev.progress, [file.name]: 0 },
        status: { ...prev.status, [file.name]: "Uploading..." },
      }));

      let uploadProgress = 0;
      const interval = setInterval(() => {
        if (uploadProgress < 100) {
          uploadProgress += 10;
          setFormData(prev => ({
            ...prev,
            progress: { ...prev.progress, [file.name]: uploadProgress },
          }));
        } else {
          clearInterval(interval);
          setFormData(prev => ({
            ...prev,
            status: { ...prev.status, [file.name]: "Completed" },
          }));
        }
      }, 50);
    });
  };

  const handleRemoveAttachment = (fileName) => {
    setFormData(prev => {
      const newAttachments = prev.attachments.filter(f => f.name !== fileName);
      const { [fileName]: _, ...newProgress } = prev.progress;
      const { [fileName]: __, ...newStatus } = prev.status;
      return {
        ...prev,
        attachments: newAttachments,
        progress: newProgress,
        status: newStatus,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      Swal.fire({
        title: 'Missing Category',
        text: 'Please select a category before submitting.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    if (!formData.subcategory) {
      Swal.fire({
        title: 'Missing Subcategory',
        text: 'Please select a subcategory before submitting.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const previewPayload = buildSubmitPayload(formData);
    const oldPayload = baselineRef.current || {};

    const { digest, html } = buildDiffPreview(oldPayload, previewPayload);
    const confirm = await Swal.fire({
      title: 'Create this ticket?',
      html: digest.length ? html : '<div style="text-align:left;font-size:13px">No differences from baseline — submitting initial ticket.</div>',
      showCancelButton: true,
      confirmButtonText: 'Create',
    });
    if (!confirm.isConfirmed) return;

    const apiPayload = {
      categoryId: previewPayload.categoryId,
      subcategoryId: previewPayload.subcategoryId,
      title: previewPayload.title,
      description: previewPayload.description,
      priority: previewPayload.priority,
      createdBy: previewPayload.createdBy,
      academyId: previewPayload.academyId,
    };

    const formDataToSend = new FormData();
    formDataToSend.append(
      'form',
      new Blob([JSON.stringify(apiPayload)], { type: 'application/json' })
    );

    formData.attachments.forEach((file) => {
      if (file.type.startsWith('image/')) {
        formDataToSend.append('photos', file);
      } else if (file.type.startsWith('video/')) {
        formDataToSend.append('videos', file);
      }
    });

    try {
      const response = await fetch(process.env.REACT_APP_API_CREATE_TICKET, {
        method: 'POST',
        headers: {
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Network or server error');
      }

      const data = await response.json();

      if (!data.success) {
        Swal.fire({
          title: 'Ticket Creation Failed!',
          text: data.success_message || 'Failed to create ticket.',
          icon: 'error',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      fireAudit({
        entity: 'ticket',
        affected_id: data?.data?.ticketId || null,
        action: 'create',
        businessApi: process.env.REACT_APP_API_CREATE_TICKET,
        businessMethod: 'POST',
        oldPayload,
        newPayload: previewPayload,
        section: 'Ticket',
        subSection: 'Create Ticket',
        extra: {
          attachmentsCount: (previewPayload.attachments || []).length,
          page: 'CreateTicket',
        },
      });

      Swal.fire({
        title: 'Ticket Created!',
        text: 'Your ticket has been created successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = previewPayload;

      navigate(-1);
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Error creating ticket.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.ct-info-label')) {
        setShowCategoryInfo(false);
        setShowSubcategoryInfo(false);
      }
    };
    if (showCategoryInfo || showSubcategoryInfo) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCategoryInfo, showSubcategoryInfo]);

  return (
    <form className="enrollment-container" onSubmit={handleSubmit}>
      <div className="breadcrumb">
        <Link to="/ticket" className="breadcrumb-link">Tickets</Link> &gt; Ticket Creation
      </div>

      {/* Academy Dropdown */}
      <div className="floating-select-form" style={{ width: '100%' }}>
        <Select
          name="academy"
          options={academyOptions}
          value={formData.academy}
          onChange={handleAcademyChange}
          placeholder="Select Academy"
          className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
          classNamePrefix="walkin-select"
          isClearable
          styles={{
            control: (provided) => ({
              ...provided,
              padding: '5px',
            }),
            placeholder: (provided) => ({
              ...provided,
              color: 'black',
            }),
          }}
        />
        {formData.academy && <label htmlFor="academy">Academy</label>}
      </div>

      <div className="ct-field ct-field-category">
        <label className="ct-info-label" style={{ position: 'relative' }}>
          Category
          <span
            style={{ marginLeft: 8, cursor: 'pointer', fontSize: '18px' }}
            onClick={e => { e.stopPropagation(); setShowCategoryInfo(s => !s); }}
            tabIndex={0}
            title="Info"
            aria-label="Category info"
          >
            <svg fill="#000000" height="20" width="20" viewBox="0 0 494.04 494.04" aria-hidden="true">
              <g>
                <path d="M247.024,69.989c-97.622,0-177.035,79.414-177.035,177.032c0,97.615,79.413,177.03,177.035,177.03 c97.614,0,177.026-79.415,177.026-177.03C424.051,149.403,344.638,69.989,247.024,69.989z M279.956,366.259 c0,5.767-4.678,10.446-10.453,10.446h-44.966c-5.775,0-10.454-4.68-10.454-10.446V231.733c0-5.765,4.678-10.445,10.454-10.445 h44.966c5.775,0,10.453,4.68,10.453,10.445V366.259z M247.024,181.148c-22.163,0-40.145-17.971-40.145-40.142 c0-22.168,17.981-40.14,40.145-40.14c22.156,0,40.136,17.972,40.136,40.14C287.16,163.177,269.18,181.148,247.024,181.148z"></path>
                <path d="M247.024,0C110.602,0,0.001,110.597,0.001,247.021c0,136.422,110.601,247.019,247.023,247.019 s247.015-110.597,247.015-247.019C494.039,110.597,383.447,0,247.024,0z M247.024,456.987 c-115.781,0-209.965-94.192-209.965-209.965c0-115.777,94.184-209.969,209.965-209.969c115.772,0,209.966,94.192,209.966,209.969 C456.99,362.795,362.796,456.987,247.024,456.987z"></path>
              </g>
            </svg>
          </span>
        </label>
        {showCategoryInfo && (
          <div className="ct-info-popup">
            {formData.category && formData.category.description
              ? formData.category.description
              : "No description available for this category."}
          </div>
        )}
        <div className="ct-card-group">
          {categories.length === 0 ? (
            <div style={{ color: 'red', fontWeight: 'bold', margin: '10px 0' }}>
              No categories found.
            </div>
          ) : (
            categories.map(cat => {
              const slug = slugify(cat.name);
              const isSel = formData.category && formData.category.id === cat.id;
              return (
                <div
                  key={cat.id}
                  className={[
                    'ct-card',
                    'ct-category-card',
                    `ct-category-${slug}`,
                    isSel && 'ct-selected',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleCategoryClick(cat)}
                >
                  {cat.name}
                </div>
              );
            })
          )}
        </div>
      </div>

      {formData.category && (
        <div className="ct-field ct-field-subcategory">
          <label className="ct-info-label" style={{ position: 'relative' }}>
            Subcategory
            <span
              style={{ marginLeft: 8, cursor: 'pointer', fontSize: '18px' }}
              onClick={e => { e.stopPropagation(); setShowSubcategoryInfo(s => !s); }}
              tabIndex={0}
              title="Info"
              aria-label="Subcategory info"
            >
              <svg fill="#000000" height="20" width="20" viewBox="0 0 494.04 494.04" aria-hidden="true">
                <g>
                  <path d="M247.024,69.989c-97.622,0-177.035,79.414-177.035,177.032c0,97.615,79.413,177.03,177.035,177.03 c97.614,0,177.026-79.415,177.026-177.03C424.051,149.403,344.638,69.989,247.024,69.989z M279.956,366.259 c0,5.767-4.678,10.446-10.453,10.446h-44.966c-5.775,0-10.454-4.68-10.454-10.446V231.733c0-5.765,4.678-10.445,10.454-10.445 h44.966c5.775,0,10.453,4.68,10.453,10.445V366.259z M247.024,181.148c-22.163,0-40.145-17.971-40.145-40.142 c0-22.168,17.981-40.14,40.145-40.14c22.156,0,40.136,17.972,40.136,40.14C287.16,163.177,269.18,181.148,247.024,181.148z"></path>
                  <path d="M247.024,0C110.602,0,0.001,110.597,0.001,247.021c0,136.422,110.601,247.019,247.023,247.019 s247.015-110.597,247.015-247.019C494.039,110.597,383.447,0,247.024,0z M247.024,456.987 c-115.781,0-209.965-94.192-209.965-209.965c0-115.777,94.184-209.969,209.965-209.969c115.772,0,209.966,94.192,209.966,209.969 C456.99,362.795,362.796,456.987,247.024,456.987z"></path>
                </g>
              </svg>
            </span>
          </label>
          {showSubcategoryInfo && (
            <div className="ct-info-popup">
              {formData.subcategory && formData.subcategory.description
                ? formData.subcategory.description
                : "No description available for this subcategory."}
            </div>
          )}
          <div className="ct-card-group">
            {formData.category && formData.category.subcategories && formData.category.subcategories.length === 0 ? (
              <div style={{ color: 'red', fontWeight: 'bold', margin: '10px 0' }}>
                No subcategories found.
              </div>
            ) : (
              formData.category && formData.category.subcategories && formData.category.subcategories.map(sub => {
                const slug = slugify(sub.name);
                const isSel = formData.subcategory && formData.subcategory.id === sub.id;
                return (
                  <div
                    key={sub.id}
                    className={[
                      'ct-card',
                      'ct-subcategory-card',
                      `ct-subcategory-${slug}`,
                      isSel && 'ct-selected',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSubcategoryClick(sub)}
                  >
                    {sub.name}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="ct-field ct-field-priority">
        <label>Priority</label>
        <div className="ct-card-group">
          {priorities.map(level => {
            const slug = slugify(level);
            const isSel = formData.priority === level;
            return (
              <div
                key={level}
                className={[
                  'ct-card',
                  'ct-priority-card',
                  `ct-priority-${slug}`,
                  isSel && 'ct-selected',
                ].filter(Boolean).join(' ')}
                onClick={() => handlePriorityClick(level)}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="floating-select-form">
        <input
          type="text"
          name="title"
          className={`walkin-form ${formData.title ? 'has-value' : ''}`}
          value={formData.title}
          onChange={handleChange}
          placeholder="Short summary"
          required
        />
        {formData.title && <label>Title</label>}
      </div>

      <div className="floating-select-form">
        <textarea
          name="description"
          className={`walkin-form-comments ${formData.description ? 'has-value' : ''}`}
          value={formData.description}
          onChange={handleChange}
          placeholder="Detailed description..."
          rows={4}
          required
        />
        {formData.description && <label>Description</label>}
      </div>

      <div className="file-upload-form">
        <label style={{ color: 'black', fontSize: '18px', fontWeight: 'bold' }}>Attachments</label>
        <div className="uploaded-files">
          {formData.attachments.map((file, index) => {
            const progress = formData.progress[file.name] || 0;
            const status = formData.status[file.name] || 'Uploading...';

            return (
              <div key={index} className="file-progress" style={{ display: 'flex', flexDirection: 'column', margin: '10px' }}>
                <div className="file-info">
                  <p className="file-name">{file.name}</p>
                  <p className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  <button
                    type="button"
                    className="remove-attachment-btn"
                    title="Remove"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#f44336',
                      fontSize: '20px',
                      cursor: 'pointer',
                      marginLeft: '10px',
                      fontWeight: 'bold',
                      position: 'relative',
                      top: '-5px'
                    }}
                    onClick={() => handleRemoveAttachment(file.name)}
                  >
                    ×
                  </button>
                </div>

                <div className="progress-container" style={{ width: '200px' }}>
                  <div className="progress-bar" style={{ backgroundColor: '#e0e0e0', borderRadius: '5px' }}>
                    <div
                      className="progress-fill"
                      style={{
                        width: `${progress}%`,
                        height: '10px',
                        backgroundColor: '#76c7c0',
                        borderRadius: '5px'
                      }}
                    />
                  </div>
                  <div className="progress-text">{status}</div>
                </div>
              </div>
            );
          })}
        </div>

        {formData.attachments.length === 0 ? (
          <label htmlFor="file" className="file-upload-label">
            <div className="file-upload-design">
              <svg viewBox="0 0 640 512" height="1em">
                <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"></path>
              </svg>
              <p style={{ margin: '2px' }}>Drag and Drop</p>
              <p style={{ margin: '2px' }}>or</p>
              <span className="browse-button">Browse file</span>
            </div>
            <input
              id="file"
              type="file"
              multiple
              accept="video/*,image/*"
              onChange={handleFileChange}
            />
          </label>
        ) : (
          <label htmlFor="file" style={{ width: 'auto' }}>
            <span className="plusButton">
              <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <g mask="url(#mask0_21_345)">
                  <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                </g>
              </svg>
            </span>
            <input
              id="file"
              type="file"
              multiple
              accept="video/*,image/*"
              onChange={handleFileChange}
            />
          </label>
        )}
      </div>

      <button type="submit" className="submit-button">
        Create Ticket
      </button>
    </form>
  );
};

export default CreateTicket;
